/* Display utilities */

import siteConfig from './siteConfig.json'

export const getColor = index => [ "green", "teal", "blue", "violet", "purple", "pink", "red", "orange", "yellow", "olive", ][ index % 10 ]

export const categoryColorMap = {
  'Counseling': 'blue',
  'Psychiatric Care': 'purple',
  'Substance Use': 'green',
  'Crisis Services': 'red',
  'Belonging & Connection': 'teal',
  'Basic Needs': 'orange',
  'Disability': 'olive',
  'Education & Workforce': 'yellow',
  'Emergency Services': 'red',
  'Faith Based': 'violet',
  'Family Life': 'green',
  'Healthcare': 'pink',
  'Legal': 'brown',
  'LGBTQ+': 'pink',
  'Resiliency Building': 'teal',
  'Technology': 'grey',
  'Transportation': 'orange',
  'Violence & Justice': 'purple',
}

export const categoryHexColors = {
  'Counseling': '#4A90D9',
  'Psychiatric Care': '#9B59B6',
  'Substance Use': '#27AE60',
  'Crisis Services': '#E74C3C',
  'Belonging & Connection': '#3498DB',
  'Basic Needs': '#E67E22',
  'Disability': '#1ABC9C',
  'Education & Workforce': '#F39C12',
  'Emergency Services': '#C0392B',
  'Faith Based': '#8E44AD',
  'Family Life': '#2ECC71',
  'Healthcare': '#E91E63',
  'Legal': '#795548',
  'LGBTQ+': '#FF6B6B',
  'Resiliency Building': '#00BCD4',
  'Technology': '#607D8B',
  'Transportation': '#FF9800',
  'Violence & Justice': '#9C27B0',
}

export const normalizeCategories = (categoryValue) => {
  if (!categoryValue) return []

  if (Array.isArray(categoryValue)) {
    return [...new Set(categoryValue.map(c => `${c}`.trim()).filter(Boolean))]
  }

  if (typeof categoryValue === 'string') {
    const raw = categoryValue.trim()
    if (!raw) return []
    try {
      const parsed = JSON.parse(raw)
      if (Array.isArray(parsed)) {
        return [...new Set(parsed.map(c => `${c}`.trim()).filter(Boolean))]
      }
    } catch {}

    const parts = raw.split(/\s*(?:\|\||;|\n)\s*/).map(c => c.trim()).filter(Boolean)
    return [...new Set(parts.length > 0 ? parts : [raw])]
  }

  return []
}

const getPrimaryCategory = (categoryValue) => normalizeCategories(categoryValue)[0] || ''
const categoryMatches = (listingCategoryValue, filterCategory) => {
  if (!filterCategory) return true
  const categories = normalizeCategories(listingCategoryValue)
  if (filterCategory.endsWith(':')) {
    const prefix = filterCategory.replace(/:$/, ': ')
    return categories.some(cat => cat.startsWith(prefix))
  }
  return categories.includes(filterCategory)
}

export const getCategoryHexColor = (category) => {
  const primaryCategory = getPrimaryCategory(category)
  if (!primaryCategory) return '#4A90D9'
  const parent = primaryCategory.split(': ')[0]
  return categoryHexColors[parent] || '#7F8C8D'
}

export const getCategoryColor = (category) => {
  const primaryCategory = getPrimaryCategory(category)
  if (!primaryCategory) return 'blue'
  const parent = primaryCategory.split(': ')[0]
  return categoryColorMap[parent] || 'blue'
}
export const formatSocialMediaUrl = url => url.includes('https://www.') ? url.split('https://www.')[1] : url.includes('https://') ? url.split('https://')[1] : url.includes('http://') ? url.split('http://')[1] : url
export const titleCaseKey = key => key.charAt(0).toUpperCase() + key.slice(1)

/* Calculations used by the dropdown menus in MapSearch */

const getListingCity = (listing, cities) => {
  if (listing.city?.trim()) return listing.city.trim()
  const addr = (listing.full_address || '').toLowerCase()
  if (!addr) return null
  for (const city of cities) {
    if (addr.includes(city.toLowerCase())) return city
  }
  return null
}

export const getCityCount = (listings, cities = []) => {
  const cityList = cities.length ? cities : (siteConfig.cities || [])
  let cityCount = {}
  for (let i = 0; i < listings.length; i++) {
    const city = getListingCity(listings[i], cityList)
    if (city) {
      if (cityCount[city]) cityCount[city]++
      else cityCount[city] = 1
    }
  }
  return cityCount
}

const HIDDEN_KEYWORDS = ['Faith-Based', 'Hidden']

function ensureArray(val) {
  if (Array.isArray(val)) return val
  if (typeof val === 'string') {
    try { const p = JSON.parse(val); if (Array.isArray(p)) return p } catch {}
    const cleaned = val.replace(/^\{|\}$/g, '').trim()
    if (cleaned) return cleaned.split(',').map((s) => s.replace(/^"|"$/g, '').trim()).filter(Boolean)
    return []
  }
  return []
}

export const getServiceDeliveryCount = (listings) => {
  const count = {}
  ;(listings || []).forEach((l) => {
    const arr = ensureArray(l.service_delivery)
    arr.forEach((v) => { count[v] = (count[v] || 0) + 1 })
  })
  return count
}

export const getKeywordCount = listings => {
  let keywordCount = {}
  listings.forEach((listing) => {
    if (listing.keywords) {
      ensureArray(listing.keywords).forEach((keyword) => {
        if (HIDDEN_KEYWORDS.includes(keyword)) return
        if (!keywordCount[`${keyword}`]) keywordCount[`${keyword}`] = 1
        else keywordCount[`${keyword}`] ++
      })
    }
  })
  return keywordCount
}

export const costKeywords = ['OHP', 'Private Insurance', 'Financial Aid Available', 'Free', 'Accepts Uninsured', 'Sliding Scale', 'Low Cost', 'Medicare / Medicaid']

export const getCostCount = listings => {
  let costCount = {}
  listings.forEach((listing) => {
    if (listing.cost_keywords) {
      ensureArray(listing.cost_keywords).forEach((keyword) => {
        if (!costCount[`${keyword}`]) costCount[`${keyword}`] = 1
        else costCount[`${keyword}`] ++
      })
    }
  })
  return costCount
}

export const getCategoryCount = (listings) => {
  let listingCategories = {}
  listings.forEach((listing) => {
    normalizeCategories(listing.category).forEach((category) => {
      const [ parentCategory, subCategory ] = category.split(`: `)
      if (!parentCategory || !subCategory) return
      if (!listingCategories[`${parentCategory}`]) listingCategories[`${parentCategory}`] = {}
      if (!listingCategories[`${parentCategory}`][`${subCategory}`]) listingCategories[`${parentCategory}`][`${subCategory}`] = 1
      else listingCategories[`${parentCategory}`][`${subCategory}`] ++
    })
  })
  return listingCategories
}

/* This function accepts json listings and returns them, formatted for leaflet use */
export function formatListings(listings) {
  listings = listings.map(listing => {
    if (listing.keywords) listing.keywords = ensureArray(listing.keywords)
    if (listing.cost_keywords && listing.cost_keywords?.length > 0) {
      listing.cost = listing.cost_keywords
    }
    return listing
  })
  return listings.map(({latitude, longitude, ...listing}) => ({coords: [latitude, longitude], ...listing}))
}

/* Primary search function used by Map */
export function filterListings(listings = {}, searchParams, search = "", hidden=[], { hideFaithBased = false, ageGroupFilter = 'all' } = {}) {
  // if URL includes the "saved" param, display saved listings ONLY
  if (searchParams.get('saved')) {
    let savedGuids = searchParams.getAll('saved')
    return listings.filter(listing => savedGuids.includes(listing.guid.toString()))
  }

  const { age, tag, age_group, service_delivery: serviceDeliveryFilter, ...filters } = Object.fromEntries(searchParams)
  const searchFunction = (listing) => {
    const isHidden = (hidden.includes(listing.guid))
    if (isHidden) return false

    if (hideFaithBased) {
      if (listing.keywords && listing.keywords.includes('Faith-Based')) return false
      if (normalizeCategories(listing.category).some(category => category.startsWith('Faith Based'))) return false
    }

    if (listing.keywords && listing.keywords.includes('Hidden') && !search && !tag) return false

    if (ageGroupFilter !== 'all') {
      const group = listing.age_group || 'Youth and Adult'
      if (ageGroupFilter === 'Youth' && group === 'Adult') return false
      if (ageGroupFilter === 'Adult' && group === 'Youth') return false
    }

    const listingEntries = Object.entries(listing).join(' ').toLowerCase()

    if (serviceDeliveryFilter) {
      const delivery = ensureArray(listing.service_delivery)
      const legacyKw = ensureArray(listing.keywords)
      const hasDelivery = delivery.includes(serviceDeliveryFilter) ||
        (serviceDeliveryFilter === 'Online' && legacyKw.some(k => /online/i.test(k))) ||
        (serviceDeliveryFilter === 'Telehealth' && legacyKw.some(k => /telehealth/i.test(k))) ||
        (serviceDeliveryFilter === 'In-Person' && legacyKw.some(k => /in-?person/i.test(k)))
      if (!hasDelivery) return false
    }

    if (tag) {
      let hasTag = listingEntries.includes(tag.toLowerCase())
      if (!hasTag) return false
    }

    if (search) {
      let hasSearchTerm = listingEntries.includes(search.toLowerCase())
      if (!hasSearchTerm) return false
    }

    if (filters) {
      let hasFilters = Object.entries(filters).every(([ key, value ]) => {
        if (key === 'category') {
          return categoryMatches(listing.category, value)
        }
        if (key === 'city') {
          const listingCity = getListingCity(listing, siteConfig.cities || [])
          return listingCity === value
        }
        return Array.isArray(listing[key]) ? listing[key].includes(value) : listing[key] === value
      })
      if (!hasFilters) return false
    }

    if (age) {
      let isCorrectAge = !age || ((!listing.max_age || age <= listing.max_age) && (!listing.min_age || age >= listing.min_age))
      if (!isCorrectAge) return false
    }
    return true
  }
  
  return listings.filter(searchFunction)
}